import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Table,
  Text,
  Group,
  Badge,
  TextInput,
  Skeleton,
} from '@mantine/core';
import { IconSearch, IconReceipt2, IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useProvisionalCosts } from '@/hooks/usePortalApi';

interface ProvisionalCostItem {
  id: number;
  product_name: string;
  provisional_cost: string;
  status: 'provisional' | 'final';
  cost_variance: string;
}

type SortField = 'product_name' | 'provisional_cost' | 'status' | 'cost_variance';

export default function ProvisionalCosts() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('product_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading } = useProvisionalCosts();
  const items: ProvisionalCostItem[] = (data?.items ?? []) as ProvisionalCostItem[];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.product_name.toLowerCase().includes(s) ||
          r.status.toLowerCase().includes(s),
      );
    }
    result.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortField];
      const bv = (b as unknown as Record<string, unknown>)[sortField];
      const cmp = (av as string) < (bv as string) ? -1 : (av as string) > (bv as string) ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [items, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  const renderVariance = (variance: string) => {
    const value = parseFloat(variance);
    if (value === 0) {
      return (
        <Text size="sm" c="dimmed">
          {'\u2014'}
        </Text>
      );
    }

    const isPositive = value > 0;
    return (
      <Group gap={4}>
        {isPositive ? (
          <IconArrowUp size={14} color="var(--mantine-color-red-6)" />
        ) : (
          <IconArrowDown size={14} color="var(--mantine-color-green-6)" />
        )}
        <Text size="sm" fw={500} c={isPositive ? 'red' : 'green'}>
          {isPositive ? '+' : ''}
          {value.toFixed(2)}%
        </Text>
      </Group>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Provisional Costs"
        description="Track provisional and finalized costs for your orders"
      />

      <TextInput
        placeholder="Search by product name..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconReceipt2 size={30} />}
          title="No provisional costs"
          description={
            search
              ? 'No items match your search criteria'
              : 'There are no provisional cost records available'
          }
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('product_name')}
                >
                  Product Name{sortIndicator('product_name')}
                </Table.Th>
                <Table.Th
                  ta="right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('provisional_cost')}
                >
                  Provisional Cost{sortIndicator('provisional_cost')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('status')}
                >
                  Status{sortIndicator('status')}
                </Table.Th>
                <Table.Th
                  ta="right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('cost_variance')}
                >
                  Cost Variance{sortIndicator('cost_variance')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {row.product_name}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      ${parseFloat(row.provisional_cost).toFixed(2)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {row.status === 'final' ? (
                      <Badge size="sm" variant="light" color="green">
                        final
                      </Badge>
                    ) : (
                      <Badge size="sm" variant="light" color="yellow">
                        provisional
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">{renderVariance(row.cost_variance)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
